import { SendHorizontal } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePurchaseOrderSendButton } from '@/features/purchase-order/hooks/use-purchase-order-send-button';
import { PurchaseOrderSendDialog } from '@/features/purchase-order/purchase-order-send-dialog';

type PurchaseOrderSendButtonProps = {
  purchaseOrderId: string;
};

export function PurchaseOrderSendButton({
  purchaseOrderId,
}: PurchaseOrderSendButtonProps) {
  const { t } = useTranslation('purchaseOrder');
  const [open, setOpen] = useState(false);
  const blockedReasonId = useId();
  const {
    availableWarehouses,
    canSendPurchaseOrder,
    handleSendPurchaseOrder,
    isSendingPurchaseOrder,
    initialRecipientEmails,
    initialRecipientLabels,
    selectedWarehouse,
    sendingBlockedReason,
  } = usePurchaseOrderSendButton({ purchaseOrderId });

  if (!canSendPurchaseOrder) {
    return null;
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <fieldset
            aria-describedby={
              sendingBlockedReason ? blockedReasonId : undefined
            }
            aria-label={t('sendPo')}
            className='m-0 inline-flex min-w-0 rounded-md border-0 p-0 focus-visible:outline focus-visible:outline-ring'
            tabIndex={sendingBlockedReason ? 0 : undefined}
          >
            <Button
              animate='scale'
              disabled={Boolean(sendingBlockedReason) || isSendingPurchaseOrder}
              onClick={() => setOpen(true)}
              size='lg'
              type='button'
            >
              {t('sendPo')}
              <SendHorizontal />
            </Button>
          </fieldset>
        </TooltipTrigger>
        {sendingBlockedReason ? (
          <TooltipContent>{sendingBlockedReason}</TooltipContent>
        ) : null}
      </Tooltip>
      {sendingBlockedReason ? (
        <span className='sr-only' id={blockedReasonId}>
          {sendingBlockedReason}
        </span>
      ) : null}
      <PurchaseOrderSendDialog
        availableWarehouses={availableWarehouses}
        initialRecipientEmails={initialRecipientEmails}
        initialRecipientLabels={initialRecipientLabels}
        isSending={isSendingPurchaseOrder}
        onOpenChange={setOpen}
        onSend={handleSendPurchaseOrder}
        open={open}
        purchaseOrderId={purchaseOrderId}
        selectedWarehouseId={selectedWarehouse.id}
        sendingBlockedReason={sendingBlockedReason}
      />
    </>
  );
}
